/* eslint-disable arrow-body-style */
import { mapActions } from 'vuex';
import { toCents } from '@/utils/helpers';
import loaderMixin from '@/mixins/loader';
import log from '@/logger';
import Contract from '@/models/contract';
import { getActiveButtonAfterSwitch } from '@/components/market/v2/market-contract-horizontal-v2/market-contract-horizontal-v2';
import { MODALS } from '@/components/app/app-modals/app-modals';

export default {
  mixins: [
    loaderMixin,
  ],
  props: {
    slim: {
      type: Boolean,
      default: false,
    },
    contract: {
      type: Object,
      required: true,
    },
    orderBook: {
      type: Object,
      required: true,
    },
    buyOffers: {
      type: Array,
      default: [],
    },
    sellOffers: {
      type: Array,
      default: [],
    },
    operationValue: {
      type: String,
      default: 'yes',
      validator: value => ['no', 'yes'].indexOf(value) >= 0,
    },
    operationType: {
      type: String,
      default: 'buy',
    },
  },
  data() {
    return {
      hoveredIndex: null,
      leftPricesUnavailable: true,
      rightPricesUnavailable: true,
      sideActive: 'left',
    };
  },
  watch: {
    orderBook() {
      this.leftPricesUnavailable = true;
      this.rightPricesUnavailable = true;
    },
  },
  computed: {
    contractModel() {
      return new Contract(this.contract);
    },
    tableModifiers() {
      return {
        'order-book__table': true,
        'order-book__table--slim': this.slim,
      };
    },
    leftSideNoOffersModifiers() {
      return {
        column: true,
        'small-6': true,
        'order-book__unavailable': true,
        'order-book__unavailable--active': (this.sideActive === 'left'),
      };
    },
    rightSideNoOffersModifiers() {
      return {
        column: true,
        'small-6': true,
        'order-book__unavailable': true,
        'order-book__unavailable--right': true,
        'order-book__unavailable--active': (this.sideActive === 'right'),
      };
    },
    leftSideHeaderModifiers() {
      return {
        column: true,
        'small-6': true,
        'order-book__header-cell': true,
        'order-book__header-cell--highlight-light': !(this.sideActive === 'left'),
        'order-book__header-cell--highlight-strong': this.sideActive === 'left',
      };
    },
    rightSideHeaderModifiers() {
      return {
        column: true,
        'small-6': true,
        'order-book__header-cell': true,
        'order-book__header-cell--highlight-light': !(this.sideActive === 'right'),
        'order-book__header-cell--highlight-strong': this.sideActive === 'right',
      };
    },
    isSharesOwned() {
      return this.contract.userQuantity > 0 || this.buyOffers.length > 0;
    },
    operationValueText() {
      return this.operationValue.charAt(0).toUpperCase() + this.operationValue.slice(1);
    },
    operationTypeLeftText() {
      return 'Buy';
    },
    operationTypeRightText() {
      return 'Sell';
    },
    operationValueRightText() {
      // const opposite = this.operationValue === 'yes' ? 'No' : 'Yes';
      return this.operationValueText;
    },
    pricesGrouped() {
      const prices = [];
      const yesOrders = this.orderBook ? this.orderBook.yesOrders || [] : [];
      const noOrders = this.orderBook ? this.orderBook.noOrders || [] : [];

      let leftPrices = [...yesOrders];
      let rightPrices = [...noOrders];
      let leftField = 'pricePerShare';
      let rightField = 'costPerShareYes';
      let leftOffers = this.sellOffers;
      let rightOffers = this.buyOffers;
      this.sideActive = 'left';

      if (this.isSharesOwned) {
        if ((this.operationValue === 'yes' && this.operationType === 'buy') || (this.operationType === 'sell' && this.operationValue === 'no')) {
          leftPrices = [...yesOrders];
          rightPrices = [...noOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareYes';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...noOrders];
            rightPrices = [...yesOrders];
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
            leftField = 'pricePerShare';
            rightField = 'costPerShareNo';
          }
        } else {
          leftPrices = [...noOrders];
          rightPrices = [...yesOrders];
          leftField = 'pricePerShare';
          rightField = 'costPerShareNo';
          leftOffers = this.sellOffers;
          rightOffers = this.buyOffers;
          if (this.operationType === 'sell') {
            this.sideActive = 'right';
            leftPrices = [...yesOrders];
            rightPrices = [...noOrders];
            leftField = 'pricePerShare';
            rightField = 'costPerShareYes';
            leftOffers = this.sellOffers;
            rightOffers = this.buyOffers;
          }
        }
      } else if (this.operationValue === 'no') {
        rightField = 'costPerShareNo';
        leftPrices = [...noOrders];
        rightPrices = [...yesOrders];
      }

      const leftOffersField = leftField;
      const rightOffersField = rightField;

      const max = leftPrices.length > rightPrices.length ? leftPrices.length : rightPrices.length;

      for (let i = 0; i < max; i += 1) {
        if (leftPrices[i]) {
          this.leftPricesUnavailable = false;
        }
        if (rightPrices[i]) {
          this.rightPricesUnavailable = false;
        }

        prices.push({
          index: i,
          buyPrice: leftPrices[i] ? toCents(leftPrices[i][leftField]) : null,
          buyQuantity: leftPrices[i] ? leftPrices[i].quantity : null,
          buyOwned: leftPrices[i] ?
            leftOffers.find(s => s.pricePerShare === leftPrices[i][leftOffersField])
            !== undefined : false,
          sellPrice: rightPrices[i] ? toCents(rightPrices[i][rightField]) : null,
          sellQuantity: rightPrices[i] ? rightPrices[i].quantity : null,
          sellOwned: rightPrices[i] ?
            rightOffers.find(s => s.pricePerShare === rightPrices[i][rightOffersField])
              !== undefined : false,
        });
      }
      return prices;
    },
    pricesOwned() {
      return this.pricesGrouped.find(item => item.buyOwned || item.sellOwned);
    },
    firstItemForActiveSideOwned() {
      return this.pricesGrouped.find((item) => {
        return (this.sideActive === 'right') ? item.sellOwned : item.buyOwned;
      });
    },
  },
  methods: {
    ...mapActions('popup', ['setGenericSmallPopup']),
    ...mapActions('trade', ['getOrderBookTradeInfo', 'toggleTradeOperation']),
    getTradeType(operationType, operationValue) {
      if (operationType === 'buy' && operationValue === 'no') return 0;
      if (operationType === 'buy' && operationValue === 'yes') return 1;
      if (operationType === 'sell' && operationValue === 'no') return 2;
      if (operationType === 'sell' && operationValue === 'yes') return 3;
      return 1;
    },
    onMouseOver(index, side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = index;
    },
    onMouseLeave(side) {
      if (!(this.sideActive === side)) return;
      this.hoveredIndex = null;
    },
    headerStrongModifier(side) {
      const isActive = side === this.sideActive;
      return {
        'order-book__header-cell--legend-light': !isActive,
        'order-book__header-cell--legend-strong': isActive,
      };
    },
    hoverQuantityClassModifier(owned, index, side) {
      const isSideActive = this.sideActive === side;
      return {
        'order-book__body-cell--owned': owned,
        'order-book__body-cell--hover': (isSideActive && this.hoveredIndex === index),
        'order-book__body-cell--clickable': isSideActive,
        'order-book__body-cell--inactive': !isSideActive,
      };
    },
    showTradeInfo(item, operationType, operationValue, side) {
      const price = (side === 'right') ? item.sellPrice : item.buyPrice;
      const payload = {
        tradeType: this.getTradeType(operationType, operationValue),
        contractId: this.contract.contractId,
        price,
      };
      this.loaderAdd(document.body);
      this.getOrderBookTradeInfo(payload)
        .then((data) => {
          setTimeout(() => {
            this.loaderRemove(document.body);
            this.setGenericSmallPopup({
              name: MODALS.SMALL_OFFERS_INDICATOR,
              options: {
                items: data,
                operationType,
                price,
              },
            });
          }, 400);
        })
        .catch(() => this.loaderRemove(document.body));
    },
    handleClick(item, isMine, operationType, operationValue, side) {
      if (
        !isMine &&
        this.firstItemForActiveSideOwned &&
        item.index > this.firstItemForActiveSideOwned.index) {
        return;
      }
      if (isMine) {
        this.showTradeInfo(item, operationType, operationValue, side);
        return;
      }
      let quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
        return prev + curr.buyQuantity;
      }, 0);
      let price = this.pricesGrouped[item.index].buyPrice;

      if (side === 'right') {
        quantity = this.pricesGrouped.slice(0, item.index + 1).reduce((prev, curr) => {
          return prev + curr.sellQuantity;
        }, 0);
        price = this.pricesGrouped[item.index].sellPrice;
      }

      if (price === null) return;
      if (operationType === 'sell' && quantity > this.contract.userQuantity) {
        quantity = this.contract.userQuantity;
      }
      this.$emit('click', {
        quantity,
        price,
      });
    },
    onClick(item, isMine, side) {
      if (this.sideActive !== side) {
        log.info(`[ORDER BOOK]: switching side from ${this.sideActive} to ${side}`);
        // this.toggleTradeOperation(side);
        const button = getActiveButtonAfterSwitch(this.contractModel, side);
        if (isMine) {
          this.showTradeInfo(item, button.type, button.value, side);
        }
      } else {
        this.handleClick(item, isMine, this.operationType, this.operationValue, this.sideActive);
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/order-book/order-book-prices/order-book-prices.js